<script>
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAI } from 'dashboard/composables/useAI';
import { OPEN_AI_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';

import NextButton from 'dashboard/components-next/button/Button.vue';

const PROVIDERS = [
  {
    id: 'openai',
    translationKey: 'OPEN_AI',
    helpUrl: 'https://www.chatwoot.com/blog/v2-17',
  },
  {
    id: 'perplexity',
    translationKey: 'PERPLEXITY_AI',
    helpUrl: 'https://docs.perplexity.ai/docs/api-getting-started',
  },
];

export default {
  components: {
    NextButton,
  },
  emits: ['close'],

  setup() {
    const { updateUISettings } = useUISettings();
    const { recordAnalytics } = useAI();
    const v$ = useVuelidate();

    return { updateUISettings, v$, recordAnalytics };
  },
  data() {
    return {
      value: '',
      selectedProvider: 'openai',
    };
  },
  validations: {
    value: {
      required,
    },
  },
  computed: {
    providers() {
      return PROVIDERS;
    },
    providerTranslationKey() {
      return (
        this.providers.find(provider => provider.id === this.selectedProvider)
          ?.translationKey || 'OPEN_AI'
      );
    },
    providerHelpUrl() {
      return (
        this.providers.find(provider => provider.id === this.selectedProvider)
          ?.helpUrl || 'https://www.chatwoot.com/blog/v2-17'
      );
    },
    providerOptions() {
      return this.providers.map(provider => ({
        label:
          this.$t(
            `INTEGRATION_SETTINGS.${provider.translationKey}.NAME_SHORT`
          ) || provider.translationKey,
        value: provider.id,
      }));
    },
    modalTitle() {
      return this.$t(
        `INTEGRATION_SETTINGS.${this.providerTranslationKey}.CTA_MODAL.TITLE`
      );
    },
    modalDescription() {
      return this.$t(
        `INTEGRATION_SETTINGS.${this.providerTranslationKey}.CTA_MODAL.DESC`
      );
    },
    keyPlaceholder() {
      return this.$t(
        `INTEGRATION_SETTINGS.${this.providerTranslationKey}.CTA_MODAL.KEY_PLACEHOLDER`
      );
    },
    needHelpLabel() {
      return this.$t(
        `INTEGRATION_SETTINGS.${this.providerTranslationKey}.CTA_MODAL.BUTTONS.NEED_HELP`
      );
    },
    dismissLabel() {
      return this.$t(
        `INTEGRATION_SETTINGS.${this.providerTranslationKey}.CTA_MODAL.BUTTONS.DISMISS`
      );
    },
    finishLabel() {
      return this.$t(
        `INTEGRATION_SETTINGS.${this.providerTranslationKey}.CTA_MODAL.BUTTONS.FINISH`
      );
    },
  },
  watch: {
    providerTranslationKey() {
      this.value = '';
      this.v$.$reset();
    },
  },
  methods: {
    onClose() {
      this.$emit('close');
    },

    onDismiss() {
      useAlert(
        this.$t(
          `INTEGRATION_SETTINGS.${this.providerTranslationKey}.CTA_MODAL.DISMISS_MESSAGE`
        )
      );
      this.updateUISettings({
        is_open_ai_cta_modal_dismissed: true,
      });
      this.onClose();
    },

    async finishAISetup() {
      const payload = {
        app_id: this.selectedProvider,
        settings: {
          api_key: this.value,
        },
      };
      try {
        await this.$store.dispatch('integrations/createHook', payload);
        this.alertMessage = this.$t(
          `INTEGRATION_SETTINGS.${this.providerTranslationKey}.CTA_MODAL.SUCCESS_MESSAGE`
        );
        this.recordAnalytics(
          OPEN_AI_EVENTS.ADDED_AI_INTEGRATION_VIA_CTA_BUTTON
        );
        this.onClose();
      } catch (error) {
        const errorMessage = error?.response?.data?.message;
        this.alertMessage =
          errorMessage || this.$t('INTEGRATION_APPS.ADD.API.ERROR_MESSAGE');
      } finally {
        useAlert(this.alertMessage);
      }
    },
    openProviderDoc() {
      window.open(this.providerHelpUrl, '_blank');
    },
  },
};
</script>

<template>
  <div class="flex-1 min-w-0 px-0">
    <woot-modal-header
      :header-title="modalTitle"
      :header-content="modalDescription"
    />
    <div class="px-8 pt-2 pb-0">
      <woot-select
        v-model="selectedProvider"
        :options="providerOptions"
        :placeholder="
          $t('INTEGRATION_SETTINGS.GENERAL.PROVIDER_SELECTOR.PLACEHOLDER')
        "
      />
    </div>
    <form
      class="flex flex-col flex-wrap modal-content"
      @submit.prevent="finishAISetup"
    >
      <div class="w-full mt-2">
        <woot-input
          v-model="value"
          type="text"
          :class="{ error: v$.value.$error }"
          :placeholder="keyPlaceholder"
          @blur="v$.value.$touch"
        />
      </div>
      <div class="flex flex-row justify-between w-full gap-2 px-0 py-2">
        <NextButton
          ghost
          type="button"
          class="!px-3"
          :label="needHelpLabel"
          @click.prevent="openProviderDoc"
        />
        <div class="flex items-center gap-1">
          <NextButton
            faded
            slate
            type="reset"
            :label="dismissLabel"
            @click.prevent="onDismiss"
          />
          <NextButton
            type="submit"
            :disabled="v$.value.$invalid"
            :label="finishLabel"
          />
        </div>
      </div>
    </form>
  </div>
</template>
